import { Link } from "react-router-dom";
import { PAGE_HEROES } from "../content/page-copy";
import { motion } from "framer-motion";
import {
  Container,
  Section,
  SectionHead,
  Card,
  buttonStyles,
  fadeUp,
  stagger,
  inView,
} from "@comai-portal/design-system";
import { LiveHonestyGauge } from "../components/HonestyGauge";

/**
 * TrustPage — the mission made a destination. (CO 069, deepened CO 069 sweep)
 *
 * The differentiator, carried with conviction: the mission pair (claims that
 * survive the closest read / infrastructure you can verify), trustworthiness as
 * the eighth -ility, the three grains, the keystone (trustworthy because it
 * doesn't trust itself), and why that matters for agents acting on your
 * business. Operator-respecting depth, not vibes.
 */
const HERO = PAGE_HEROES['trust']!;

export function TrustPage() {
  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      {/* Hero — the mission */}
      <Section>
        <Container width="prose" className="text-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="ds-eyebrow mb-5">
              {HERO.eyebrow}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5vw,3.5rem)] leading-[1.08] text-balance"
            >
              {HERO.h1}
            </motion.h1>
            <motion.p
              variants={fadeUp}
              className="text-lead text-mute dark:text-mute-soft mt-6"
            >
              Most companies ask you to believe their claims. ComOS is built so
              you don't have to. Trust here isn't a value on a slide — it's a
              mechanism, running continuously, observable as a live rate, and
              decaying the moment it stops. We build it the way we build
              everything else: as a property of the system.
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      {/* CO 613 — the live ratio, on this page. Trust and Honesty share one
          header slot, so the number the argument rests on is shown here rather
          than one click away. Same component and fetch as /honesty. */}
      <Section tint="sunk">
        <Container width="prose">
          <motion.div {...inView} variants={fadeUp}>
            <LiveHonestyGauge />
            <p className="mt-4 text-center text-sm text-mute dark:text-mute-soft">
              <Link
                to="/honesty"
                className="underline text-signal dark:text-signal-bright"
              >
                What this number counts →
              </Link>
            </p>
          </motion.div>
        </Container>
      </Section>

      {/* The mission pair */}
      <Section>
        <Container>
          <SectionHead
            align="center"
            eyebrow="The mission, both sides"
            title="Two sentences we hold ourselves to."
          />
          <motion.div
            {...inView}
            variants={stagger}
            className="mt-10 grid gap-5 md:grid-cols-2 max-w-3xl mx-auto"
          >
            <motion.div variants={fadeUp}>
              <Card accent className="h-full text-center">
                <p className="ds-eyebrow mb-3">What we build toward</p>
                <p className="text-display-sm">
                  Claims that survive the closest read.
                </p>
                <p className="text-sm text-mute dark:text-mute-soft mt-3">
                  Every statement on this site, in the product, in the docs —
                  meant to hold up when a skeptic checks it, not just when a
                  buyer skims it.
                </p>
              </Card>
            </motion.div>
            <motion.div variants={fadeUp}>
              <Card accent className="h-full text-center">
                <p className="ds-eyebrow mb-3">What you get</p>
                <p className="text-display-sm">
                  Infrastructure you can verify.
                </p>
                <p className="text-sm text-mute dark:text-mute-soft mt-3">
                  Not a trust-us. A surface that exposes the evidence behind
                  every claim, so you can confirm it yourself instead of taking
                  our word.
                </p>
              </Card>
            </motion.div>
          </motion.div>
        </Container>
      </Section>

      {/* Trustworthiness is an -ility */}
      <Section tint="sunk">
        <Container width="prose">
          <motion.div {...inView} variants={fadeUp}>
            <p className="ds-eyebrow mb-4">Trustworthiness is an -ility</p>
            <h2 className="text-display-md text-balance">
              We treat trust like reliability or security — a quality the system
              generates, not a feeling it asks for.
            </h2>
            <p className="text-lead text-mute dark:text-mute-soft mt-6">
              Reliability is a rate: uptime, sustained by a mechanism, decaying
              if the mechanism stops. Trustworthiness is the same shape —
              verifiable trust, generated continuously by mechanism, decaying if
              it stops. That reframing is the whole point: a feeling can't be
              engineered, but a rate can. So we built the mechanism.
            </p>
          </motion.div>
        </Container>
      </Section>

      {/* The three grains */}
      <Section>
        <Container>
          <SectionHead
            eyebrow="How trust is generated"
            title="Three mechanisms, always on."
            lead="When they run, trust is generated. If they stopped, it would decay. That's what makes it real instead of rhetorical."
          />
          <motion.div
            {...inView}
            variants={stagger}
            className="mt-12 grid gap-5 md:grid-cols-3"
          >
            {[
              {
                n: "1",
                k: "Proof before done",
                v: "Every change to the OS passes a verification gate before it ships. Nothing is declared finished until its central claim is demonstrated — not asserted. Per-response honesty.",
              },
              {
                n: "2",
                k: "Honest revision",
                v: "When something is wrong, a change order opens, runs the same gate, and the correction propagates through the record. The system revises itself in the open — the trail shows what changed and why.",
              },
              {
                n: "3",
                k: "A live honesty rate",
                v: "The honesty ratio is published live — the fraction of the record that has passed its gate, climbing as claims become more defensible over time. The aggregate trust basis, visible — to you.",
              },
            ].map((it) => (
              <motion.div key={it.k} variants={fadeUp}>
                <Card hover className="h-full">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-signal text-white font-grotesk text-sm font-semibold">
                      {it.n}
                    </span>
                    <h3 className="text-display-sm">{it.k}</h3>
                  </div>
                  <p className="text-mute dark:text-mute-soft text-sm">
                    {it.v}
                  </p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </Container>
      </Section>

      {/* The keystone — trustworthy because it doesn't trust itself */}
      <Section tint="sunk">
        <Container width="prose" className="text-center">
          <motion.div {...inView} variants={fadeUp}>
            <p className="ds-eyebrow mb-4">Why it holds</p>
            <h2 className="text-display-md text-balance">
              ComOS is trustworthy because it doesn't trust itself.
            </h2>
            <p className="text-lead text-mute dark:text-mute-soft mt-6">
              The gate refuses to let the system sign off on its own unproven
              output. The change-order discipline assumes any decision might be
              wrong and forces it to justify itself. The honesty ratio measures
              whether the ground under each claim was actually clean.
              Self-distrust, mechanized — the same geometry runs through the
              whole system, from how an agent's action is governed to how a
              number gets published. It's the reason the CEO is no longer
              the load-bearing part of the belief. The substrate is.
            </p>
          </motion.div>
        </Container>
      </Section>

      {/* Why it matters for agents on your business */}
      <Section>
        <Container>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <SectionHead
              eyebrow="What this buys you"
              title="The same discipline governs the agents acting on your business."
              lead="A fleet of autonomous agents runs your operation. The reason that's safe instead of reckless is the same self-distrust that governs the OS itself — nothing acts unchecked, every action is evidenced, and you can audit any of it."
            />
            <motion.div {...inView} variants={stagger} className="grid gap-4">
              {[
                "Every agent action is gated, scoped, and logged — QC at the operating layer.",
                "When an agent gets it wrong, the record shows it and it's reversible — honest revision, not a black box.",
                "You can read the evidence for any decision the system made — the audit trail is yours to query.",
              ].map((t) => (
                <motion.div key={t} variants={fadeUp}>
                  <Card className="text-sm">{t}</Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </Container>
      </Section>

      {/* The proof surface — CTA */}
      <Section tint="ink">
        <Container width="prose" className="text-center">
          <motion.div {...inView} variants={fadeUp}>
            <h2 className="text-display-md text-balance text-paper">
              The honesty ratio is the proof.
            </h2>
            <p className="text-lead text-paper/70 mt-5">
              Not a screenshot. Not a promise. A live rate, generated
              continuously by the QC gate — climbing as the record gets proven,
              and visible the whole way up. We don't transmit conviction. We
              hand you the number.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/honesty"
                className={buttonStyles({ variant: "primary", size: "lg" })}
              >
                See it live →
              </Link>
              <Link
                to="/security"
                className={buttonStyles({
                  variant: "outline",
                  size: "lg",
                  className:
                    "border-paper/25 text-paper hover:text-white hover:border-paper",
                })}
              >
                See the security posture
              </Link>
            </div>
          </motion.div>
        </Container>
      </Section>
    </div>
  );
}
